// Shared builders that turn a study into clean, paste-ready plain text, plus a
// printable (Save-as-PDF) document. Used by the in-app study page and the public
// shared-study page so copy/print behave identically everywhere.

// STANDING RULE: anything that can leave the user's private account (copied text,
// printed/PDF documents, share links) must carry a UTM-tagged link back to the app
// plus a short "try it free" CTA. These helpers centralise that so every surface stays
// compliant. The phrase is reworded per context; the link is always UTM-tagged.
const APP_ORIGIN = () =>
  (typeof window !== "undefined" && window.location && window.location.origin) || "https://openwordai.com";

export function ctaUrl(medium = "copy", campaign = "study_export") {
  const p = new URLSearchParams({ utm_source: "export", utm_medium: medium, utm_campaign: campaign });
  return `${APP_ORIGIN()}/welcome?${p.toString()}`;
}

// Plain-text CTA block (two lines: reworded phrase + the tagged link) for copy-to-clipboard.
export function ctaTextLine(phrase, medium = "copy", campaign = "study_export") {
  return `${phrase}\n${ctaUrl(medium, campaign)}`;
}

// HTML CTA (clickable) for printable / PDF documents.
export function ctaHtmlLink(phrase, medium = "pdf", campaign = "study_export") {
  return `<a href="${ctaUrl(medium, campaign)}">${phrase}</a>`;
}

function versesLines(verses) {
  return (verses || []).map(
    (v) => `${v.verse ? v.verse + ". " : ""}${(v.text || "").trim()}`
  );
}

// Just the passage — reference, translation, numbered verses, copyright.
export function buildScriptureText(study, passage) {
  const L = [];
  const meta = [study?.passage_reference, passage?.translation_name].filter(Boolean).join(" \u00b7 ");
  if (meta) L.push(meta);
  L.push("");
  versesLines(passage?.verses).forEach((line) => L.push(line));
  if (passage?.copyright) { L.push(""); L.push(passage.copyright); }
  L.push(""); L.push("\u2014");
  L.push(ctaTextLine("Read & study any passage free with OpenWord AI:", "copy", "study_export"));
  return L.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

// The whole study — title, overview, scripture, sections + reflect prompts, summary, exercise, prayer.
export function buildStudyText(study, passage, opts = {}) {
  const { prayer } = opts;
  const verses = passage?.verses || [];
  const scriptureText = verses.map((v) => (v.text || "").trim()).filter(Boolean).join(" ");
  const L = [];
  if (study?.title) L.push(study.title);
  const meta = [study?.passage_reference, passage?.translation_name].filter(Boolean).join(" \u00b7 ");
  if (meta) L.push(meta);
  L.push("");
  if (study?.overview) { L.push(study.overview); L.push(""); }
  if (scriptureText) {
    L.push(study?.passage_reference || "Scripture");
    versesLines(verses).forEach((line) => L.push(line));
    L.push("");
  }
  (study?.sections || []).forEach((sec) => {
    if (sec.heading) L.push(sec.heading);
    if (sec.commentary) L.push(sec.commentary);
    (sec.reflect || []).forEach((q) => L.push(`\u2022 Reflect: ${q}`));
    L.push("");
  });
  if (study?.summary) { L.push("Summary"); L.push(study.summary); L.push(""); }
  if (study?.exercise) { L.push("An exercise for you"); L.push(study.exercise); L.push(""); }
  if (prayer) { L.push("A prayer"); L.push(prayer); L.push(""); }
  L.push("\u2014");
  L.push(ctaTextLine("Study any passage free with OpenWord AI:", "copy", "study_export"));
  return L.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

// --- Individual pieces of a study (copy one section at a time) ---
// Each carries the standing-rule UTM CTA so copied text always links back to the app.
function _pieceWithCta(lines) {
  const L = [...lines, "", "\u2014",
    ctaTextLine("Study any passage free with OpenWord AI:", "copy", "study_export")];
  return L.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

export function buildOverviewText(study) {
  const L = [];
  if (study?.title) L.push(study.title);
  if (study?.passage_reference) L.push(study.passage_reference);
  L.push("");
  if (study?.overview) L.push(study.overview);
  return _pieceWithCta(L);
}

export function buildSectionText(sec) {
  const L = [];
  if (sec?.heading) { L.push(sec.heading); L.push(""); }
  if (sec?.commentary) L.push(sec.commentary);
  (sec?.reflect || []).forEach((q) => L.push(`\u2022 Reflect: ${q}`));
  return _pieceWithCta(L);
}

export function buildSummaryText(study) {
  return _pieceWithCta(["Summary", "", study?.summary || ""]);
}

export function buildExerciseText(study) {
  return _pieceWithCta(["An exercise for you", "", study?.exercise || ""]);
}

export function buildAllSectionsText(study) {
  const L = [];
  if (study?.title) L.push(study.title);
  if (study?.passage_reference) L.push(study.passage_reference);
  L.push("");
  (study?.sections || []).forEach((sec) => {
    if (sec.heading) { L.push(sec.heading); L.push(""); }
    if (sec.commentary) L.push(sec.commentary);
    L.push("");
  });
  return _pieceWithCta(L);
}

export function buildStrongsText(e) {
  const L = [];
  const head = [e?.original, e?.transliteration, e?.strongs_number].filter(Boolean).join(" \u00b7 ");
  if (head) L.push(head);
  if (e?.word) L.push(`English: ${e.word}`);
  if (e?.definition) L.push(`Definition: ${e.definition}`);
  if (e?.usage) L.push(`In this passage: ${e.usage}`);
  return _pieceWithCta(L);
}

export function buildDeeperText(chat, title) {
  const L = [];
  if (title) { L.push(title); L.push(""); }
  (chat || []).forEach((m) => {
    const c = (m.content || "").trim();
    if (!c) return;
    L.push(`${m.role === "user" ? "You" : "OpenWord AI"}: ${c}`);
    L.push("");
  });
  return _pieceWithCta(L);
}


// The reader's OWN notes — freeform notes + any reflection answers they typed.
// Returns "" when the reader hasn't written anything yet.
export function buildNotesText(study, opts = {}) {
  const { notes, answers } = opts;
  const ref = study?.passage_reference;
  const L = [];
  if (ref) { L.push(`My notes on ${ref}`); L.push(""); }
  let has = false;
  if (notes && notes.trim()) { L.push(notes.trim()); L.push(""); has = true; }
  (study?.sections || []).forEach((sec, si) => {
    (sec.reflect || []).forEach((q, qi) => {
      const a = answers?.[`${si}-${qi}`];
      if (a && String(a).trim()) {
        L.push(`Q: ${q}`);
        L.push(`A: ${String(a).trim()}`);
        L.push("");
        has = true;
      }
    });
  });
  if (!has) return "";
  L.push("\u2014");
  L.push(ctaTextLine(`From my study on ${ref || "Scripture"} — keep studying free with OpenWord AI:`, "copy", "notes_export"));
  return L.join("\n").trim();
}

// Open a clean, print-friendly document (users can print or Save as PDF).
// Works on mobile and desktop; returns false if a popup was blocked.
function brandedShell(titleText, innerHtml, cta = {}) {
  const origin = (typeof window !== "undefined" && window.location && window.location.origin) || "";
  const printed = new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  const ctaPhrase = cta.phrase || "Study any passage free with OpenWord AI";
  const ctaLink = ctaHtmlLink(ctaPhrase, cta.medium || "pdf", cta.campaign || "study_export");
  return `<!doctype html><html><head><meta charset="utf-8"/>
<title>${titleText} — OpenWord AI</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: Georgia, 'Times New Roman', serif; color: #161D2F; line-height: 1.6;
         max-width: 720px; margin: 0 auto; padding: 32px 24px 48px; }
  .ow-header { display: flex; align-items: center; gap: 10px; padding-bottom: 14px;
               border-bottom: 2px solid #00E5FF; margin-bottom: 28px; }
  .ow-header img { width: 30px; height: 30px; }
  .ow-brand { font-family: Georgia, serif; font-size: 20px; color: #161D2F; line-height: 1; }
  .ow-tag { font-family: system-ui, sans-serif; font-size: 10px; letter-spacing: .18em;
            text-transform: uppercase; color: #00E5FF; margin-top: 3px; }
  .eyebrow { text-transform: uppercase; letter-spacing: .18em; font-size: 11px; color: #94A3B8;
             font-family: system-ui, sans-serif; margin-bottom: 6px; }
  h1 { font-size: 30px; line-height: 1.2; margin: 4px 0 8px; }
  h2 { font-size: 19px; font-family: system-ui, sans-serif; margin: 26px 0 8px; }
  .meta { color: #00E5FF; font-family: system-ui, sans-serif; font-size: 13px;
          text-transform: uppercase; letter-spacing: .1em; margin-bottom: 18px; }
  .hook { background: #10222A; border: 1px solid rgba(74,124,130,.2); border-radius: 12px;
          padding: 14px 16px; font-style: italic; }
  .scripture sup { color: #00E5FF; font-size: .6em; margin-right: 2px; }
  .italic p { font-style: italic; }
  .copyright { font-size: 12px; color: #3A4763; }
  ul.reflect { color: #1E273D; } ul.reflect li { margin: 4px 0; }
  .card { background: #07090E; border: 1px solid #1E273D; border-radius: 12px; padding: 16px 18px; margin-top: 20px; }
  .ow-footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #1E273D;
               font-family: system-ui, sans-serif; font-size: 11px; color: #3A4763; text-align: center; line-height: 1.7; }
  .ow-footer strong { color: #00E5FF; font-weight: 600; }
  @media print { body { margin: 0 auto; } a { color: inherit; text-decoration: none; } }
</style></head>
<body>
  <div class="ow-header">
    ${origin ? `<img src="${origin}/openword-logo.png" alt=""/>` : ""}
    <div><div class="ow-brand">OpenWord AI</div><div class="ow-tag">Scripture-first Bible study</div></div>
  </div>
  ${innerHtml}
  <div class="ow-footer">
    <strong>${_esc(ctaPhrase)}</strong> &middot; ${ctaLink}<br/>
    Printed ${printed} — a study tool, rooted in the original Hebrew &amp; Greek, not a replacement for a pastor, a church, or the Bible itself.
  </div>
</body></html>`;
}

function openPrint(html) {
  const w = window.open("", "_blank");
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => { try { w.print(); } catch (e) { /* user can print manually */ } }, 400);
  return true;
}

const _esc = (s) => (s || "").toString().replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const _paras = (t) => (t || "").split(/\n{2,}/).filter((p) => p.trim())
  .map((p) => `<p>${_esc(p.trim())}</p>`).join("");

export function printStudyDocument(study, passage, opts = {}) {
  const { prayer } = opts;
  const verses = passage?.verses || [];
  const meta = [study?.passage_reference, passage?.translation_name].filter(Boolean).join(" &middot; ");

  const scriptureHtml = verses.length
    ? `<section><div class="eyebrow">Scripture</div><p class="scripture">${verses
        .map((v) => `<sup>${_esc(String(v.verse || ""))}</sup> ${_esc((v.text || "").trim())}`)
        .join(" ")}</p>${passage?.copyright ? `<p class="copyright">${_esc(passage.copyright)}</p>` : ""}</section>`
    : "";

  const sectionsHtml = (study?.sections || []).map((sec) => `
    <section>
      ${sec.heading ? `<h2>${_esc(sec.heading)}</h2>` : ""}
      ${_paras(sec.commentary)}
      ${(sec.reflect || []).length
        ? `<ul class="reflect">${sec.reflect.map((q) => `<li>Reflect: ${_esc(q)}</li>`).join("")}</ul>`
        : ""}
    </section>`).join("");

  const inner = `
  <div class="meta">${meta || "OpenWord AI"}</div>
  <h1>${_esc(study?.title || "")}</h1>
  ${study?.overview ? `<p>${_esc(study.overview)}</p>` : ""}
  ${scriptureHtml}
  ${sectionsHtml}
  ${study?.summary ? `<div class="card"><div class="eyebrow">Summary</div>${_paras(study.summary)}</div>` : ""}
  ${study?.exercise ? `<div class="card"><div class="eyebrow">An exercise for you</div>${_paras(study.exercise)}</div>` : ""}
  ${prayer ? `<div class="card"><div class="eyebrow">A prayer</div>${_paras(prayer)}</div>` : ""}`;

  return openPrint(brandedShell(_esc(study?.title || "Bible study"), inner,
    { phrase: "Study any passage free with OpenWord AI", medium: "pdf", campaign: "study_export" }));
}

// A simple branded printable for a single item (a saved verse or a prayer).
export function printSimpleDocument({ title, meta, body, italic = false, cta }) {
  const inner = `
  ${meta ? `<div class="meta">${_esc(meta)}</div>` : ""}
  <h1>${_esc(title || "")}</h1>
  <div class="${italic ? "italic" : ""}">${_paras(body)}</div>`;
  return openPrint(brandedShell(_esc(title || "OpenWord AI"), inner,
    cta || { phrase: "Study any passage free with OpenWord AI", medium: "pdf", campaign: "study_export" }));
}

// Copy text for a single saved verse / prayer.
export function buildVerseText(b) {
  const meta = [b?.reference, b?.translation].filter(Boolean).join(" \u00b7 ");
  return [meta, "", (b?.text || "").trim(), "", "\u2014",
    ctaTextLine("Save & study verses free with OpenWord AI:", "copy", "verse_export")]
    .join("\n").trim();
}

export function buildPrayerText(e) {
  const head = [e?.title, e?.reference].filter(Boolean).join(" \u00b7 ");
  return [head, "", (e?.prayer || "").trim(), "", "\u2014",
    ctaTextLine("Turn Scripture into prayer free with OpenWord AI:", "copy", "prayer_export")]
    .join("\n").trim();
}

// ---- Full-journey export (Phase B): passage + the reader's notes/answers + the Go Deeper conversation ----

const roleLabel = (r) => (r === "user" ? "You" : "Companion");

// The study arc as plain-text lines (no title/CTA) — reused by single + compiled exports.
function journeyBody(study, passage, opts = {}) {
  const { prayer, notes, answers, chat } = opts;
  const verses = passage?.verses || [];
  const L = [];
  if (study?.overview) { L.push(study.overview); L.push(""); }
  if (verses.length) {
    L.push(study?.passage_reference || "Scripture");
    versesLines(verses).forEach((line) => L.push(line));
    L.push("");
  }
  (study?.sections || []).forEach((sec, si) => {
    if (sec.heading) L.push(sec.heading);
    if (sec.commentary) L.push(sec.commentary);
    (sec.reflect || []).forEach((q, qi) => {
      L.push(`\u2022 Reflect: ${q}`);
      const a = answers?.[`${si}-${qi}`];
      if (a && String(a).trim()) L.push(`   My answer: ${String(a).trim()}`);
    });
    L.push("");
  });
  if (study?.summary) { L.push("Summary"); L.push(study.summary); L.push(""); }
  if (study?.exercise) { L.push("An exercise for you"); L.push(study.exercise); L.push(""); }
  if (notes && notes.trim()) { L.push("My notes"); L.push(notes.trim()); L.push(""); }
  if (prayer) { L.push("A prayer"); L.push(prayer); L.push(""); }
  const msgs = (chat || []).filter((m) => (m.content || "").trim());
  if (msgs.length) {
    L.push("Going deeper — my conversation");
    msgs.forEach((m) => { L.push(`${roleLabel(m.role)}: ${(m.content || "").trim()}`); L.push(""); });
  }
  return L;
}

// One continuous record of a whole session: study + notes/answers + Go Deeper conversation.
export function buildJourneyText(study, passage, opts = {}) {
  const L = [];
  if (study?.title) L.push(study.title);
  const meta = [study?.passage_reference, passage?.translation_name].filter(Boolean).join(" \u00b7 ");
  if (meta) L.push(meta);
  L.push("");
  journeyBody(study, passage, opts).forEach((line) => L.push(line));
  L.push("\u2014");
  L.push(ctaTextLine("Study any passage free with OpenWord AI:", "copy", "study_journey"));
  return L.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

// The study arc as HTML (no shell) — reused by single + compiled print.
function journeyInnerHtml(study, passage, opts = {}) {
  const { prayer, notes, answers, chat } = opts;
  const verses = passage?.verses || [];
  const scriptureHtml = verses.length
    ? `<section><div class="eyebrow">Scripture</div><p class="scripture">${verses
        .map((v) => `<sup>${_esc(String(v.verse || ""))}</sup> ${_esc((v.text || "").trim())}`)
        .join(" ")}</p>${passage?.copyright ? `<p class="copyright">${_esc(passage.copyright)}</p>` : ""}</section>`
    : "";
  const sectionsHtml = (study?.sections || []).map((sec, si) => `
    <section>
      ${sec.heading ? `<h2>${_esc(sec.heading)}</h2>` : ""}
      ${_paras(sec.commentary)}
      ${(sec.reflect || []).map((q, qi) => {
        const a = answers?.[`${si}-${qi}`];
        return `<div class="reflect-item"><p class="reflect-q">Reflect: ${_esc(q)}</p>${
          a && String(a).trim() ? `<p class="reflect-a">${_esc(String(a).trim())}</p>` : ""}</div>`;
      }).join("")}
    </section>`).join("");
  const msgs = (chat || []).filter((m) => (m.content || "").trim());
  const chatHtml = msgs.length
    ? `<section><div class="eyebrow">Going deeper — my conversation</div>${msgs.map((m) =>
        `<div class="bubble ${m.role === "user" ? "me" : "ai"}"><span class="who">${roleLabel(m.role)}</span>${_paras(m.content)}</div>`
      ).join("")}</section>`
    : "";
  return `
  ${study?.overview ? `<p>${_esc(study.overview)}</p>` : ""}
  ${scriptureHtml}
  ${sectionsHtml}
  ${study?.summary ? `<div class="card"><div class="eyebrow">Summary</div>${_paras(study.summary)}</div>` : ""}
  ${study?.exercise ? `<div class="card"><div class="eyebrow">An exercise for you</div>${_paras(study.exercise)}</div>` : ""}
  ${notes && notes.trim() ? `<div class="card"><div class="eyebrow">My notes</div>${_paras(notes)}</div>` : ""}
  ${prayer ? `<div class="card"><div class="eyebrow">A prayer</div>${_paras(prayer)}</div>` : ""}
  ${chatHtml}`;
}

const JOURNEY_CSS = `
  .reflect-item { margin: 10px 0; } .reflect-q { color: #00E5FF; font-family: system-ui, sans-serif; font-size: 14px; margin: 0; }
  .reflect-a { margin: 4px 0 0; padding-left: 12px; border-left: 2px solid #1E273D; }
  .bubble { border-radius: 12px; padding: 10px 14px; margin: 8px 0; max-width: 90%; }
  .bubble .who { display: block; font-family: system-ui, sans-serif; font-size: 10px; letter-spacing: .12em; text-transform: uppercase; opacity: .7; margin-bottom: 2px; }
  .bubble.me { background: #00E5FF; color: #fff; margin-left: auto; } .bubble.me p { margin: 0; }
  .bubble.ai { background: #141B29; color: #161D2F; } .bubble.ai p { margin: 0 0 6px; }`;

export function printJourneyDocument(study, passage, opts = {}) {
  const meta = [study?.passage_reference, passage?.translation_name].filter(Boolean).join(" &middot; ");
  const inner = `<div class="meta">${meta || "OpenWord AI"}</div><h1>${_esc(study?.title || "")}</h1>${journeyInnerHtml(study, passage, opts)}`;
  const html = brandedShell(_esc(study?.title || "Bible study"), inner,
    { phrase: "Study any passage free with OpenWord AI", medium: "pdf", campaign: "study_journey" })
    .replace("</style>", `${JOURNEY_CSS}</style>`);
  return openPrint(html);
}

// ---- Compiled passage history (Phase C): every visit to one passage in one document ----

export function buildCompiledText(label, sessions = []) {
  const L = [`All my studies on ${label}`, `${sessions.length} ${sessions.length === 1 ? "visit" : "visits"}`, ""];
  sessions.forEach((s, i) => {
    const date = s.created_at ? new Date(s.created_at).toLocaleDateString() : "";
    L.push(`${"=".repeat(3)} Visit ${i + 1}${date ? ` — ${date}` : ""} ${"=".repeat(3)}`);
    if (s.study?.title) L.push(s.study.title);
    if (s.reference) L.push(s.reference);
    L.push("");
    journeyBody(s.study, s.passage, { notes: s.notes, answers: s.answers, chat: s.chat }).forEach((line) => L.push(line));
    L.push("");
  });
  L.push("\u2014");
  L.push(ctaTextLine(`Build a study on ${label} free with OpenWord AI:`, "copy", "passage_history"));
  return L.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

export function printCompiledDocument(label, sessions = []) {
  const visits = sessions.map((s, i) => {
    const date = s.created_at ? new Date(s.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "";
    return `<div class="visit"><div class="eyebrow">Visit ${i + 1}${date ? ` &middot; ${date}` : ""}</div>
      <h1 style="font-size:24px">${_esc(s.study?.title || s.reference || "")}</h1>
      ${journeyInnerHtml(s.study, s.passage, { notes: s.notes, answers: s.answers, chat: s.chat })}</div>`;
  }).join('<hr class="visit-sep"/>');
  const inner = `<div class="meta">${_esc(label)} &middot; ${sessions.length} ${sessions.length === 1 ? "visit" : "visits"}</div>
    <h1>All my studies on ${_esc(label)}</h1>${visits}`;
  const html = brandedShell(`${_esc(label)} — all studies`, inner,
    { phrase: `Build a study on ${label} free with OpenWord AI`, medium: "pdf", campaign: "passage_history" })
    .replace("</style>", `${JOURNEY_CSS} .visit-sep { border: none; border-top: 2px dashed #3A4763; margin: 36px 0; } .visit { margin-top: 8px; }</style>`);
  return openPrint(html);
}

